import { EntityTypes, type TabModel } from '@rightship-tech/common-ui/models/basic.models';
import { DialogClientService } from '@rightship-tech/common-ui/services/basic/dialog-client.service';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { autoinject, computedFrom, observable, transient } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { Router } from "aurelia-router";
import Enumerable from 'linq';
import { RightshipQiMessages } from '../../../../configuration/rightshipqi-event-messages.constants';
import { TitleWithCountHelper } from '../../../../helpers/title-with-count-helper';
import { type IAssignEntity } from "../../../../models/basics/basics.models";
import { CwsaEntityModel, CwsaStatusClientModel, CwsaStatusEnum, CwsaStepEnum, GetCwSelfAssessmentReviewResponse } from "../../../../models/cwsa/cwsa.models";
import { CompCorrespondenceModel, CorrespondenceCountsResponse, PrivateNoteCountsResponse, type DocumentsCountResponse } from '../../../../models/vessels/vessel.models';
import { AttachmentClientService } from '../../../../services/attachment/attachments-client.service';
import { CwsaService } from '../../../../services/cwsa/cwsa.service';
import { EntityCommentService } from '../../../../services/entity-comment/entity-comment.service';
import { SubtasksService } from '../../../../services/subtasks/subtasks.service';
import { Reflection } from "../../../../utilities/reflection";
import { PartialTabbedEntityItem } from "@rightship-tech/common-ui/partials/partial-tabbed-entity-item/partial-tabbed-entity-item";
import { EntityDocuments } from '../../common/entity-documents/entity-documents';
import { EntityComments } from '../../common/entity-notes/entity-notes';
import { Correspondence } from '../common-steps/correspondence/correspondence';
import { EmailHistory } from '../common-steps/email-history/email-history';
import { RightShipDialogServiceConstants } from './../../../../constants/basics/rightship-dialog.constants';
import { CwsaAssignment } from "./components/assignment/cwsa-assignment";
import { CwsaFormData } from "./tabs/form-data/cwsa-form-data";
import { CwsaHistoryTrail } from './tabs/history-trail/cwsa-history-trail';
import { CwsaReviewCriteria } from "./tabs/review-criteria/cwsa-review-criteria";
import { UserClientService } from '../../../../services/authentication/user-client.service';

@autoinject
@transient()
export class Cwsa {

    private _subscriptions: Subscription[] = [];
    public documentCounts: DocumentsCountResponse;
    public tabs: TabModel[];
    public cwsaLastVisitedTabCacheKey: string = "cwsa-last-visited-tab";
    public cwSelfAssessmentGuid: string;
    public cwSelfAssessment: GetCwSelfAssessmentReviewResponse;
    public assignment: CwsaAssignment;
    public cwsaStatus: CwsaStatusClientModel;
    public correspondenceCounts: CorrespondenceCountsResponse;
    public privateNoteCounts: PrivateNoteCountsResponse;
    public entityItem: PartialTabbedEntityItem;
    public initialSubtaskLinkedEntityId: number;
    public initialSubtaskLinkedEntityTypeId: number;
    public item: CwsaEntityModel;

    @observable
    public assignedUser: IAssignEntity;

    @computedFrom(Reflection.getPropertyPath<Cwsa>(v => v.cwsaStatus))
    public get statusIsFinal(): boolean {
        return this.cwsaStatus && (this.cwsaStatus.statusId == CwsaStatusEnum.Accepted || this.cwsaStatus.statusId == CwsaStatusEnum.Cancelled);
    }

    @computedFrom(Reflection.getPropertyPath<Cwsa>(v => v.cwsaStatus), Reflection.getPropertyPath<Cwsa>(v => v.cwSelfAssessment))
    public get showAddRfi(): boolean {
        return this.cwSelfAssessment && this.cwsaStatus && this.cwsaStatus.statusId != CwsaStatusEnum.Cancelled;
    }

    @computedFrom(Reflection.getPropertyPath<Cwsa>(v => v.router.currentInstruction))
    public get previousInstructionTitle(): string {
        let title = this.router.history.getState("previousRouteName");

        if (title && title.trim() != "" && title != this.i18N.tr('back-text'))
            return title;

        const prevInstruction = this.router.currentInstruction.previousInstruction;
        title = prevInstruction
            ? prevInstruction.config.title
            : this.i18N.tr('back-text');

        this.router.history.setState("previousRouteName", title);

        return title;
    }

    @computedFrom(Reflection.getPropertyPath<Cwsa>(v => v.cwsaStatus))
    public get isNotCompletedStatus(): boolean {
        return this.cwsaStatus && this.cwsaStatus.statusId !== CwsaStatusEnum.Completed && this.cwsaStatus.statusId !== CwsaStatusEnum.Accepted
            && this.cwsaStatus.statusId !== CwsaStatusEnum.Rejected && this.cwsaStatus.statusId !== CwsaStatusEnum.Cancelled;
    }


    @computedFrom(Reflection.getPropertyPath<Cwsa>(v => v.documentCounts))
    public get documentsCounted(): number {
        return this.documentCounts.count;
    }

    constructor(private cwsaService: CwsaService,
        private subtasksService: SubtasksService,
        private attachmentClientService: AttachmentClientService,
        private cwsaFormDataVm: CwsaFormData,
        private cwsaReviewCriteria: CwsaReviewCriteria,
        private correspondence: Correspondence,
        private documents: EntityDocuments,
        private router: Router, private i18N: I18N,
        private entityComments: EntityComments,
        private dialogClientService: DialogClientService,
        private cwsaHistoryTrail: CwsaHistoryTrail,
        private userClientService: UserClientService,
        private emailHistory: EmailHistory,
        private entityCommentService: EntityCommentService,
        private eventAggregator: EventAggregator) { }

    async activate(args: { cwSelfAssessmentGuid: string }) {
        this.cwSelfAssessmentGuid = args.cwSelfAssessmentGuid;
        await this.refreshData();
    }

    attached() {
        this._subscriptions.push(this.eventAggregator.subscribe(RightshipQiMessages.viewCorrespondenceTab, (params: {
            initialSubtaskLinkedEntityId: number,
            initialSubtaskLinkedEntityTypeId: number
        }) => {
            this.initialSubtaskLinkedEntityId = params.initialSubtaskLinkedEntityId;
            this.initialSubtaskLinkedEntityTypeId = params.initialSubtaskLinkedEntityTypeId;
            const step = this.tabs.filter(x => x.step == CwsaStepEnum.Correspondence)[0];
            this.entityItem.currentStep = step;
        }));
    }

    detached() {
        this._subscriptions.forEach(x => x.dispose());
        this._subscriptions = [];
    }

    async refreshData(refreshAssignment: boolean = false) {
        this.cwSelfAssessment = await this.cwsaService.getCwSelfAssessment(this.cwSelfAssessmentGuid);
        this.correspondenceCounts = await this.subtasksService.getCorrespondenceCountsForEntity(this.cwSelfAssessment.cwSelfAssessmentId, EntityTypes.CrewWelfare);

        //Get private notes count
        this.privateNoteCounts = await this.entityCommentService.getPrivateNoteCountsForEntity(this.cwSelfAssessment.cwSelfAssessmentId, EntityTypes.CrewWelfare);

        //Set the entity item privateNotesCount
        this.item = {
            privateNotesCount: this.privateNoteCounts.count
        };

        await this.refreshDocumentsCount(this.cwSelfAssessment.cwSelfAssessmentId);

        this.cwsaStatus = Enumerable.from(this.cwSelfAssessment.statusHistory)
            .orderByDescending(x => new Date(x.createdDate))
            .select(s => {
                return {
                    name: s.status,
                    statusId: s.statusId
                } as CwsaStatusClientModel;
            }).toArray()[0];

        this.tabs = this.getabs();

        //Refresh the assignment if needed
        if (refreshAssignment) {
            this.cwsaFormDataVm.activate({
                cwSelfAssessment: this.cwSelfAssessment,
                correspondenceCounts: this.correspondenceCounts,
                cwsaAssignment: this.assignment,
                reload: this.refreshData.bind(this)
            });

            this.cwsaReviewCriteria.activate({
                cwSelfAssessment: this.cwSelfAssessment,
                cwsaAssignment: this.assignment,
                correspondenceCounts: this.correspondenceCounts,
                reload: this.refreshData.bind(this)
            });
        }
    }

    //Refresh documents count
    async refreshDocumentsCount(id: number) {
        this.documentCounts = await this.attachmentClientService.getDocumentCountsForEntity(id, EntityTypes.CrewWelfare);
    }

    //Refresh correspondence when the assigned user changes
    assignedUserChanged(newValue) {
        if (newValue) {
            this.correspondence.activate(this.getCorrespondenceModel(newValue.assignedTo, newValue.assignedToUserName));
        }
    }

    public getabs(): TabModel[] {
        return [
            {
                show: () => true,
                title: "form data",
                step: CwsaStepEnum.FormData,
                vm: this.cwsaFormDataVm,
                getModel: () => {
                    return {
                        cwSelfAssessment: this.cwSelfAssessment,
                        correspondenceCounts: this.correspondenceCounts,
                        cwsaAssignment: this.assignment,
                        reload: this.refreshData.bind(this, true)
                    }
                }
            },
            {
                show: () => true,
                title: "review criteria",
                step: CwsaStepEnum.ReviewCriteria,
                vm: this.cwsaReviewCriteria,
                getModel: () => {
                    return {
                        cwSelfAssessment: this.cwSelfAssessment,
                        cwsaAssignment: this.assignment,
                        correspondenceCounts: this.correspondenceCounts,
                        reload: this.refreshData.bind(this, true)
                    }
                }
            },
            {
                show: () => true,
                title: () => TitleWithCountHelper.getTitle("Correspondences", this.correspondence.correspondenceCount ?? this.correspondenceCounts.count),
                step: CwsaStepEnum.Correspondence,
                vm: this.correspondence,
                getModel: () => {
                    return this.assignment.assignedEntity ?
                        this.getCorrespondenceModel(this.assignment.assignedEntity.assignedTo, this.assignment.assignedEntity.assignedToUserName) :
                        this.getCorrespondenceModel(this.cwSelfAssessment.assignedUserId, this.cwSelfAssessment.assignedTo);
                }
            },
            {
                show: () => true,
                title: () => TitleWithCountHelper.getTitle("Document", this.documentCounts.count),
                step: CwsaStepEnum.Documents,
                vm: this.documents,
                getModel: () => {
                    return {
                        entityId: this.cwSelfAssessment.cwSelfAssessmentId,
                        entityTypeId: EntityTypes.CrewWelfare,
                        documentCount: this.documentCounts
                    }
                }
            },
            {
                show: () => true,
                title: "Email History",
                step: CwsaStepEnum.EmailHistory,
                vm: this.emailHistory,
                getModel: () => {
                    return {
                        item: {
                            id: this.cwSelfAssessment.cwSelfAssessmentId,
                            entityType: EntityTypes.CrewWelfare
                        }
                    }
                }
            },
            {
                show: () => true,
                title: () => TitleWithCountHelper.getTitle("private notes", this.item.privateNotesCount),
                step: CwsaStepEnum.PrivateNotes,
                vm: this.entityComments,
                getModel: () => ({
                    canSubmit: true,
                    entityTypeId: EntityTypes.CrewWelfare,
                    entityId: this.cwSelfAssessment.cwSelfAssessmentId,
                    item: this.item
                })
            },
            {
                show: () => true,
                title: "history trail",
                step: CwsaStepEnum.HistoryTrail,
                vm: this.cwsaHistoryTrail,
                getModel: () => { return { entityId: this.cwSelfAssessment.cwSelfAssessmentId } }
            }
        ]
    }

    public navigateBack() {
        this.router.navigateBack();
    }

    openCwsaStatusDialog() {

        //If the user is not assigned to the CWSA, show the assignment dialog
        if (this.assignment?.assignedEntity?.assignedTo != this.userClientService.currentUser.id) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CwsaAssignmentDialog, {
                name: "Cwsa assessment",
                submit: this.assignment.assignToMe.bind(this.assignment),
                callback: this.openCwsaStatusDialog.bind(this),
                currentAssignment: null
            }, true, false, false);
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CwsaStatusDialog, {
                cwsaStatus: this.cwsaStatus,
                cwSelfAssessmentGuid: this.cwSelfAssessmentGuid,
                refreshAssignment: () => this.refreshData(true)
            }, true, false, false, 'right');
        }
    }

    private getCorrespondenceModel(assignedUserId: number, userName: string): CompCorrespondenceModel {
        return {
            initialSubtaskLinkedEntityId: this.initialSubtaskLinkedEntityId,
            initialSubtaskLinkedEntityTypeId: this.initialSubtaskLinkedEntityTypeId,
            correspondenceEntity: {
                entityId: this.cwSelfAssessment.cwSelfAssessmentId,
                entityTypeId: EntityTypes.CrewWelfare,
                isInconsistentState: false,
                requestedBy: null,
                liaseWithShipManagers: null,
                assignment: {
                    assignedTo: assignedUserId,
                    assignedToUserName: userName,
                    assignToMe: this.assignment.assignToMe.bind(this.assignment),
                    isAssignedToMe: () => assignedUserId == this.userClientService.currentUser.id
                }
            },
            name: "CrewWelfare",
            showAddRFI: this.showAddRfi,
            hasOutcome: this.statusIsFinal,
            emails: [],
            newDocumentAdded: async () => {
                await this.refreshDocumentsCount(this.cwSelfAssessment.cwSelfAssessmentId);
                this.documents.activate({
                    entityId: this.cwSelfAssessment.cwSelfAssessmentId,
                    entityTypeId: EntityTypes.CrewWelfare,
                    documentCount: this.documentCounts
                });
            }
        }
    }
}